import { getLocale, useIntl } from "@umijs/max";
import { Space, Tag, Typography, theme } from "antd";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";

type StreamStatus = "idle" | "connecting" | "running" | "completed" | "error";

type HttpMethod = "GET" | "POST";

interface ParsedSseEvent {
  event: string;
  data: string;
}

interface StreamPayload {
  message?: string;
  data?: unknown;
  done?: boolean;
  error?: string;
  type?: string;
}

export interface XtermSseTerminalProps {
  active: boolean;
  title?: string;
  url: string;
  requestKey?: string | number;
  method?: HttpMethod;
  body?: object;
  height?: number;
  initialOutput?: string;
  showHeader?: boolean;
  onOutputChange?: (output: string) => void;
  onStatusChange?: (status: StreamStatus) => void;
  filterItems?: string[];
}

const DEFAULT_HEIGHT = 150;
const APPROXIMATE_CELL_WIDTH = 9;
const APPROXIMATE_CELL_HEIGHT = 20;

const parseSseEvents = (
  buffer: string,
): { events: ParsedSseEvent[]; rest: string } => {
  const normalizedBuffer = buffer.replace(/\r\n/g, "\n");
  const rawEvents = normalizedBuffer.split("\n\n");

  if (rawEvents.length === 0) {
    return { events: [], rest: normalizedBuffer };
  }

  const rest = rawEvents.pop() ?? "";
  const events = rawEvents
    .map((rawEvent) => {
      const lines = rawEvent.split("\n");
      let eventName = "message";
      const dataLines: string[] = [];

      lines.forEach((line) => {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim() || "message";
          return;
        }

        if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trimStart());
        }
      });

      return {
        event: eventName,
        data: dataLines.join("\n"),
      };
    })
    .filter((item) => item.data || item.event !== "message");

  return { events, rest };
};

const normalizeOutput = (
  event: ParsedSseEvent,
): {
  content: string;
  isError: boolean;
  isDone: boolean;
} => {
  const trimmedData = event.data.trim();
  if (!trimmedData) {
    return { content: "", isError: false, isDone: false };
  }

  if (trimmedData === "[DONE]" || event.event === "done") {
    return { content: "", isError: false, isDone: true };
  }

  try {
    const payload = JSON.parse(trimmedData) as StreamPayload;
    const textContent =
      typeof payload.message === "string"
        ? payload.message
        : typeof payload.data === "string"
          ? payload.data
          : typeof payload.error === "string"
            ? payload.error
            : trimmedData;

    return {
      content: textContent,
      isError: event.event === "error" || payload.type === "error",
      isDone: payload.done === true,
    };
  } catch (_error) {
    return {
      content: trimmedData,
      isError: event.event === "error",
      isDone: false,
    };
  }
};

/* ───────── FilterBar — independent component, re-renders don't touch xterm ───────── */

interface FilterBarProps {
  items: string[];
  registerNotify: (fn: (output: string) => void) => void;
}

const parseFilterValues = (
  output: string,
  items: string[],
): Record<string, string> => {
  if (!output || items.length === 0) return {};
  const values: Record<string, string> = {};
  items.forEach((item) => {
    const escaped = item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(
      escaped + "\\s*[:=]\\s*([\\d]+(?:\\.[\\d]+)?\\s*%?)",
      "gi",
    );
    const matches = [...output.matchAll(regex)];
    if (matches.length > 0) {
      values[item] = matches[matches.length - 1][1];
    }
  });
  return values;
};

const FilterBar: React.FC<FilterBarProps> = React.memo(
  ({ items, registerNotify }) => {
    const { token } = theme.useToken();
    const [values, setValues] = useState<Record<string, string>>({});
    const valuesRef = useRef<Record<string, string>>({});
    const rafRef = useRef<number>(0);

    const scheduleUpdate = useCallback((next: Record<string, string>) => {
      const prev = valuesRef.current;
      const existingKeys = Object.keys(prev);
      const hasChanges =
        Object.keys(next).length !== existingKeys.length ||
        existingKeys.some((k) => prev[k] !== next[k]);
      if (!hasChanges) return;

      valuesRef.current = next;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setValues({ ...valuesRef.current });
        rafRef.current = 0;
      });
    }, []);

    useEffect(() => {
      registerNotify((output: string) => {
        if (!output) {
          scheduleUpdate({});
          return;
        }
        scheduleUpdate(parseFilterValues(output, items));
      });
    }, [items, registerNotify, scheduleUpdate]);

    useEffect(() => {
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
        }
      };
    }, []);

    if (items.length === 0) return null;

    return (
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {items.map((item) => (
          <div
            key={item}
            style={{
              flex: 1,
              padding: "8px 12px",
              background: token.colorFillAlter,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            <Typography.Text
              type="secondary"
              style={{
                fontSize: 12,
                display: "block",
                marginBottom: 4,
                whiteSpace: "nowrap",
              }}
              ellipsis={{ tooltip: item }}
            >
              {item}
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {values[item] || "—"}
            </Typography.Text>
          </div>
        ))}
      </div>
    );
  },
);

FilterBar.displayName = "FilterBar";

/* ─────────────────────────── XtermSseTerminal ─────────────────────────── */

const XtermSseTerminal: React.FC<XtermSseTerminalProps> = ({
  active,
  title,
  url,
  requestKey,
  method = "POST",
  body,
  height = DEFAULT_HEIGHT,
  initialOutput,
  showHeader = true,
  onOutputChange,
  onStatusChange,
  filterItems = ["Free Spin Win Rate", "Total RTP", "Base RTP", "Free RTP"],
}) => {
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const statusRef = useRef<StreamStatus>("idle");
  const outputRef = useRef("");
  const bodyRef = useRef(body);
  bodyRef.current = body;
  const onOutputChangeRef = useRef(onOutputChange);
  onOutputChangeRef.current = onOutputChange;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const [streamStatus, setStreamStatus] = useState<StreamStatus>("idle");

  const notifyFilterRef = useRef<((output: string) => void) | null>(null);

  const registerFilterNotify = useCallback(
    (fn: (output: string) => void) => {
      notifyFilterRef.current = fn;
    },
    [],
  );

  const updateStatus = useCallback(
    (nextStatus: StreamStatus) => {
      statusRef.current = nextStatus;
      setStreamStatus(nextStatus);
      onStatusChangeRef.current?.(nextStatus);
    },
    [],
  );

  const initializeTerminal = useCallback(() => {
    if (!containerRef.current || terminalRef.current) {
      return;
    }

    const terminal = new Terminal({
      cursorBlink: true,
      convertEol: true,
      fontFamily:
        'Consolas, "SFMono-Regular", Menlo, Monaco, "Courier New", monospace',
      fontSize: 13,
      lineHeight: 1.35,
      disableStdin: true,
      theme: {
        background: "#000000",
        foreground: "#f5f5f5",
        cursor: "#ffffff",
        selectionBackground: "rgba(255,255,255,0.2)",
      },
    });

    terminal.open(containerRef.current);
    terminalRef.current = terminal;
  }, [
    token.colorBgContainer,
    token.colorPrimary,
    token.colorPrimaryBg,
    token.colorText,
  ]);

  const resizeTerminal = useCallback(() => {
    const container = containerRef.current;
    const terminal = terminalRef.current;

    if (!container || !terminal) {
      return;
    }

    const nextCols = Math.max(
      20,
      Math.floor(container.clientWidth / APPROXIMATE_CELL_WIDTH),
    );
    const nextRows = Math.max(
      1,
      Math.floor(container.clientHeight / APPROXIMATE_CELL_HEIGHT),
    );

    terminal.resize(nextCols, nextRows);
  }, []);

  const scrollTerminalToBottom = useCallback(() => {
    const terminal = terminalRef.current;
    const container = containerRef.current;
    if (!terminal || !container) {
      return;
    }

    terminal.scrollToBottom();

    window.requestAnimationFrame(() => {
      const viewport = container.querySelector<HTMLElement>(".xterm-viewport");
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    });
  }, []);

  const renderOutput = useCallback(
    (content: string) => {
      const terminal = terminalRef.current;
      if (!terminal) {
        return;
      }

      terminal.clear();
      outputRef.current = content;

      notifyFilterRef.current?.(content);

      if (!content) {
        return;
      }

      const lines = content.replace(/\r\n/g, "\n").split("\n");
      lines.forEach((line) => {
        terminal.writeln(line);
      });
      scrollTerminalToBottom();
    },
    [scrollTerminalToBottom],
  );

  const writeLine = useCallback(
    (content: string) => {
      const terminal = terminalRef.current;
      if (!terminal || !content) {
        return;
      }

      const normalizedContent = content.replace(/\r?\n/g, "\r\n");
      terminal.writeln(normalizedContent);
      scrollTerminalToBottom();
      outputRef.current = outputRef.current
        ? `${outputRef.current}\n${content}`
        : content;
      onOutputChangeRef.current?.(outputRef.current);

      notifyFilterRef.current?.(outputRef.current);
    },
    [scrollTerminalToBottom],
  );

  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const startStream = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) {
      return;
    }

    stopStream();
    terminal.clear();
    outputRef.current = "";
    onOutputChange?.("");
    notifyFilterRef.current?.("");
    updateStatus("connecting");
    writeLine("[system] connecting...");

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const headers: HeadersInit = {
      Accept: "text/event-stream",
      Language: getLocale(),
    };
    const accessToken = localStorage.getItem("token");
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    if (method === "POST") {
      headers["Content-Type"] = "application/json";
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: method === "POST" ? JSON.stringify(bodyRef.current ?? {}) : undefined,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Empty response stream");
      }

      updateStatus("running");
      writeLine("[system] connected");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let bufferedText = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        bufferedText += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseEvents(bufferedText);
        bufferedText = rest;

        events.forEach((event) => {
          const output = normalizeOutput(event);

          if (output.content) {
            const prefix = output.isError ? "[error] " : "";
            writeLine(`${prefix}${output.content}`);
          }

          if (output.isDone) {
            updateStatus("completed");
          }
        });
      }

      if (statusRef.current !== "completed") {
        updateStatus("completed");
      }
      writeLine("[system] stream closed");
    } catch (error) {
      if (abortController.signal.aborted) {
        writeLine("[system] stream stopped");
        updateStatus("idle");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Unexpected stream error";
      writeLine(`[error] ${errorMessage}`);
      updateStatus("error");
    } finally {
      abortControllerRef.current = null;
    }
  }, [method, stopStream, updateStatus, url, writeLine]);

  const intl = useIntl();

  const statusTag = useMemo(() => {
    const statusMap: Record<StreamStatus, { color: string; label: string }> = {
      idle: {
        color: "default",
        label: intl.formatMessage({ id: "component.xterm.status.idle" }),
      },
      connecting: {
        color: "processing",
        label: intl.formatMessage({ id: "component.xterm.status.connecting" }),
      },
      running: {
        color: "success",
        label: intl.formatMessage({ id: "component.xterm.status.running" }),
      },
      completed: {
        color: "blue",
        label: intl.formatMessage({ id: "component.xterm.status.completed" }),
      },
      error: {
        color: "error",
        label: intl.formatMessage({ id: "component.xterm.status.error" }),
      },
    };

    const currentStatus = statusMap[streamStatus];
    return <Tag color={currentStatus.color}>{currentStatus.label}</Tag>;
  }, [streamStatus, intl]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    initializeTerminal();
    resizeTerminal();
  }, [initializeTerminal, resizeTerminal]);

  useEffect(() => {
    if (!active) {
      stopStream();
      return;
    }

    const timer = window.setTimeout(() => {
      initializeTerminal();
      resizeTerminal();
      void startStream();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    active,
    initializeTerminal,
    requestKey,
    resizeTerminal,
    startStream,
    stopStream,
    updateStatus,
  ]);

  useEffect(() => {
    if (active) {
      return;
    }

    initializeTerminal();
    resizeTerminal();
    renderOutput(initialOutput ?? "");
    updateStatus(initialOutput ? "completed" : "idle");
  }, [
    active,
    initialOutput,
    initializeTerminal,
    renderOutput,
    resizeTerminal,
    updateStatus,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      resizeTerminal();
    });

    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, [active, resizeTerminal]);

  useEffect(() => {
    return () => {
      stopStream();
      terminalRef.current?.dispose();
      terminalRef.current = null;
    };
  }, [stopStream]);

  return (
    <div>
      {showHeader ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Space size={8}>
            {title ? <Typography.Text strong>{title}</Typography.Text> : null}
            {statusTag}
          </Space>
        </div>
      ) : null}
      <FilterBar items={filterItems} registerNotify={registerFilterNotify} />
      <div
        style={{
          height,
          width: "100%",
          background: "#000000",
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: 8,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={containerRef}
          className={styles.terminalContainer}
          style={{
            height: "100%",
            width: "100%",
          }}
        />
      </div>
    </div>
  );
};

export default React.memo(XtermSseTerminal);
